import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import PageHeader from '../../components/layout/PageHeader'
import Location from '../../hooks/Location'
import { isSuperAdminTenant } from '../../utils/superAdminTenant'
import useIntelligenceSweep from '../../hooks/useIntelligenceSweep'
import useAgenda from '../../hooks/useAgenda'
import useBenchmarkRows from '../../hooks/useBenchmarkRows'
import usePublishedCellKeys from '../../hooks/usePublishedCellKeys'
import usePublishedInsights from '../../hooks/usePublishedInsights'
import SweepStatus from '../../components/intelligence/SweepStatus'
import ThemeList from '../../components/intelligence/ThemeList'
import OutputBenchmarkList from '../../components/intelligence/OutputBenchmarkList'
import { runSweepNow } from '../../helpers/intelligence/intelligenceWrites'
import { themeDetailPath, materialBenchmarkPath } from '../../helpers/intelligence/themeRoute'
import buildBenchmarkOverview from '../../helpers/intelligence/buildBenchmarkOverview'
import { GROEI_OUTPUT, NIVEAU_OUTPUT } from '../../helpers/dashboard/buildBenchmarkRows'

// Effecten is de themalijst (doelgroep × effect). Outputs en materialen hebben
// geen doelgroep en dus geen thema; zonder eigen tabblad vielen ze van de
// pagina (besluit user 2026-10-12, herziet het tabbladloze ontwerp van
// 2026-09-14). `sources` zijn de namen uit useBenchmarkRows' failedSources.
const TABS = [
    { id: 'effecten', label: 'Effecten' },
    { id: 'outputs', label: 'Outputs', kind: GROEI_OUTPUT, sources: ['participatieladder', 'arbeidsvaardigheden'], emptyText: 'Er zijn geen output-benchmarks.' },
    { id: 'materialen', label: 'Materialen', kind: NIVEAU_OUTPUT, sources: ['materialen'], emptyText: 'Er zijn geen materialen-benchmarks.' },
]

/**
 * De Intelligence-pagina: op het tabblad Effecten één lijst van thema's
 * (doelgroep × effect), geordend op publiceerbaarheid. Alles wat over een thema
 * bekend is staat op zijn detailpagina. De tabbladen Outputs en Materialen tonen
 * de benchmarks van de meetstandaard-outputs, die geen thema kunnen zijn.
 *
 * De nachtelijke controle en de signalering staan boven de lijst: de controle
 * loopt over alle thema's, en de signalering is materiaal voor de hele redactie.
 *
 * AI-CONTEXT: See docs/features/intelligence-thema-eerst.md
 */
const IntelligenceContent = () => {
    const history = useHistory()
    const client = Location()[1]

    // De trede per thema komt uit gegevens die de pagina hier één keer ophaalt.
    const agenda = useAgenda()
    const cellKeys = usePublishedCellKeys()
    const published = usePublishedInsights()
    // De benchmark per effect: de tweede poort naast P2 (ADR-040). Een thema
    // heet pas publiceerbaar als de benchmark van zijn effect de klantdrempel
    // haalt — anders weigert de server de publicatie.
    //
    // Dit is een fan-out over de hele catalogus (één aanroep per effect), en
    // dus niet gratis. Er is geen goedkopere bron: de sterkte volgt uit de
    // tellingen die msiBenchmark uitrekent, en een tweede, lichtere telling
    // ernaast zou een tweede model zijn.
    // AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js
    const benchmarkRijen = useBenchmarkRows()
    // Dezelfde rijopbouw als het benchmarkoverzicht, zodat 'drempel gehaald'
    // hier hetzelfde betekent als daar.
    // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
    const benchmarks = useMemo(() => ({
        loaded: benchmarkRijen.loaded,
        rows: buildBenchmarkOverview(benchmarkRijen.rows),
    }), [benchmarkRijen.loaded, benchmarkRijen.rows])

    const [tab, setTab] = useState('effecten')
    const actief = TABS.find((entry) => entry.id === tab)
    // Al opgehaald voor de poort hierboven; de outputs reizen in dezelfde
    // fan-out mee, dus de tabbladen kosten geen extra aanroep.
    const tabRijen = useMemo(
        () => (actief.kind ? benchmarks.rows.filter((row) => row.kind === actief.kind) : []),
        [benchmarks.rows, actief.kind],
    )

    const [sweepBusy, setSweepBusy] = useState(false)
    const [sweepError, setSweepError] = useState('')
    const sweepState = useIntelligenceSweep()

    const startSweep = async () => {
        setSweepBusy(true)
        setSweepError('')
        try {
            await runSweepNow({})
        } catch (err) {
            setSweepError(err.message || 'Controle starten mislukt')
        } finally {
            setSweepBusy(false)
        }
    }

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={'Intelligence'}
                    AI={false}
                />

                <SweepStatus
                    sweep={sweepState.sweep}
                    neverRan={sweepState.neverRan}
                    loading={sweepState.loading}
                    error={sweepState.error}
                    busy={sweepBusy}
                    onRun={startSweep}
                />
                {sweepError && <p className="intelligence-state--error">{sweepError}</p>}

                {/* Signalering: nadrukkelijk GEEN wachtrij. Geen aantal-badge,
                    geen afhandelknop — alleen de mededeling dat er materiaal
                    ligt dat de celpoort haalt maar nog niet buiten staat. Alleen
                    themacellen (segment × mechanisme/activiteit); elke regel
                    linkt naar zijn thema, en een cel met een concept zegt dat.
                    Oudere momentopnames dragen geen themeId: dan geen link.

                    De nachtelijke controle kent alleen P2 — de celpoort. Of de
                    benchmark van het effect publiceren toelaat (ADR-040) staat
                    op het thema zelf; daarom zegt de kop hier 'celpoort' en
                    niet 'publiceerbaar'. Dat is geen tekortkoming van de
                    controle maar een keuze: de benchmark per effect uitrekenen
                    is een fan-out over de hele catalogus.
                    AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js */}
                {sweepState.sweep && sweepState.sweep.UnlinkedCells
                    && sweepState.sweep.UnlinkedCells.length > 0 && (
                    <div className="intelligence-panel">
                        <p className="intelligence-panel__title">
                            Celpoort gehaald, nog niet gepubliceerd
                        </p>
                        <p className="intelligence-panel__hint">
                            Cellen die de celpoort (P2) halen maar in geen enkele publicatie zitten.
                            Of er ook echt gepubliceerd kan worden hangt daarnaast aan de benchmark
                            van het effect; dat staat op het thema. Geen actielijst — een hint dat
                            er misschien een uitspraak blijft liggen.
                        </p>
                        <div className="intelligence-signal-list">
                            {sweepState.sweep.UnlinkedCells.map((cell) => {
                                const inhoud = (
                                    <>
                                        <span className="intelligence-signal__label">
                                            {cell.effectName ? `${cell.effectName} · ` : ''}{cell.label}
                                            {cell.segmentLabel && <span className="intelligence-signal__segment"> bij {cell.segmentLabel}</span>}
                                        </span>
                                        {cell.conceptInsightId && <span className="intelligence-badge">concept aanwezig</span>}
                                        <span>{cell.classification}</span>
                                        <span>{cell.nOrgs} org. · {cell.nRespondents} resp.</span>
                                    </>
                                )
                                return cell.themeId
                                    ? (
                                        <button
                                            key={cell.cellId}
                                            type="button"
                                            className="intelligence-signal intelligence-signal--link"
                                            onClick={() => history.push(themeDetailPath(client, cell.themeId))}
                                        >
                                            {inhoud}
                                        </button>
                                    )
                                    : <div key={cell.cellId} className="intelligence-signal">{inhoud}</div>
                            })}
                        </div>
                        {sweepState.sweep.UnlinkedTotal > sweepState.sweep.UnlinkedCells.length && (
                            <p className="intelligence-panel__hint">
                                Er zijn er nog {sweepState.sweep.UnlinkedTotal - sweepState.sweep.UnlinkedCells.length} meer;
                                deze lijst toont de sterkste.
                            </p>
                        )}
                    </div>
                )}

                <div className="intelligence-tabs" role="tablist">
                    {TABS.map((entry) => (
                        <button
                            key={entry.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === entry.id}
                            className={`intelligence-tab${tab === entry.id ? ' intelligence-tab--active' : ''}`}
                            onClick={() => setTab(entry.id)}
                        >
                            {entry.label}
                        </button>
                    ))}
                </div>

                {tab === 'effecten' && (
                    <ThemeList
                        agendaRows={agenda.rows}
                        // Zolang de publicatiesleutels niet geladen zijn, mag geen
                        // thema 'publiceerbaar' heten — null zegt de helper dat.
                        publishedCellKeys={cellKeys.loaded ? cellKeys.keys : null}
                        publishedInsights={published.insights}
                        benchmarks={benchmarks}
                        onOpen={(id) => history.push(themeDetailPath(client, id))}
                    />
                )}
                {actief.kind && (
                    <div className="intelligence-panel">
                        <OutputBenchmarkList
                            rows={tabRijen}
                            loaded={benchmarkRijen.outputsLoaded === true}
                            failedSources={(benchmarkRijen.failedSources || []).filter((naam) => actief.sources.includes(naam))}
                            emptyText={actief.emptyText}
                            // Alleen materialen hebben een detailpagina: de uitsplitsing
                            // naar de contextkenmerken van energiearmoede.
                            onOpen={actief.kind === NIVEAU_OUTPUT
                                ? (row) => history.push(materialBenchmarkPath(client, row.output))
                                : null}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

/**
 * De poort staat om de inhoud heen en niet erin: zo draait geen enkele hook
 * voor iemand die er niet hoort te zijn.
 *
 * Dit is een weergavekeuze. De échte poort zit server-side op de Cloud
 * Functions (verifyCallerOwnsCompagny) — frontend-gating is geen beveiliging.
 * AI-CONTEXT: See docs/pitfalls.md#security-cf-admin-tenant-bypass
 */
const Intelligence = () => {
    const client = Location()[1]

    if (!isSuperAdminTenant(client)) {
        return (
            <div className="main">
                <div className="main-container">
                    <p className="intelligence-state">
                        Deze pagina is alleen beschikbaar voor het Deccos-beheerteam.
                    </p>
                </div>
            </div>
        )
    }

    return <IntelligenceContent />
}

export default Intelligence
